import { Box, FlexLayout, H3, H4 } from '@deity/falcon-ui';
import { DefinitionLoader } from '@deity-io/di-data-structure-editor';
import { Button, Divider, Icon, Input } from '@deity-io/portal-ui';
import { Skeleton } from '@material-ui/lab';
import Authentication from 'components/WebServices/WebServiceSettings/Authentication';
import Configuration from 'components/WebServices/WebServiceSettings/Configuration';
import Connection from 'components/WebServices/WebServiceSettings/Connection';
import HTTPHeaders from 'components/WebServices/WebServiceSettings/HTTPHeaders';
import PaginationSettings from 'components/WebServices/WebServiceSettings/PaginationSettings';
import { WebServiceActionTypes, WebServiceContext } from 'contexts/WebServiceContext';
import _ from 'lodash';
import type { FC } from 'react';
import { createRef, useContext, useState } from 'react';
import { useGetModelQuery } from 'services/graphql/generated/hooks';

type WebServiceSettingProps = {
  isEditable: boolean;
  params: any;
};

export const WebServiceSetting: FC<WebServiceSettingProps> = ({ isEditable, params }) => {
  const ctx = useContext(WebServiceContext);

  const { data: modelData } = useGetModelQuery({
    variables: {
      packageVersionUuid: ctx?.state.packageVersionUuid || '',
      uuid: (ctx?.state.schema?.responseStructure as string) || ''
    },
    skip: !(ctx?.state.packageVersionUuid && ctx?.state.uuid),
    fetchPolicy: 'cache-first'
  });

  const updateConfig = (key: string[], value: any, set: boolean = true) => {
    if (ctx) {
      if (set) {
        ctx.dispatch({ type: WebServiceActionTypes.SET_VALUE, payload: { key, value } });
      } else {
        ctx.dispatch({ type: WebServiceActionTypes.UNSET_VALUE, payload: { key } });
      }
    }
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const Loading: FC<{ height: string }> = ({ height }) => <Skeleton width="100%" height={height} />;

  const [isHeaderEdit, setIsHeaderEdit] = useState(false);

  const nameRef = createRef<HTMLInputElement>();
  const descriptionRef = createRef<HTMLInputElement>();

  return (
    <>
      {!_.isEmpty(ctx?.state.schema) ? (
        <Box>
          <Box p="xxl">
            <FlexLayout justifyContent="space-between" width="full">
              <Box width="full">
                <FlexLayout flexDirection="column">
                  <H3 color="secondary" fontWeight="bold" fontSize="xs">
                    NAME
                  </H3>
                  <FlexLayout alignItems="center" width="full">
                    {!ctx ? (
                      <Loading height="36.39px" />
                    ) : (
                      <>
                        <H4 color="primaryText" fontSize={'xll' as any} mr="sm">
                          {isHeaderEdit ? (
                            <Input inputRef={nameRef} type="text" defaultValue={ctx?.state.name} />
                          ) : (
                            ctx?.state.name
                          )}
                        </H4>
                        {isEditable && (
                          <Button
                            startIcon={<Icon icon={isHeaderEdit ? 'checkCircle' : 'edit'} />}
                            color="primary"
                            onClick={() => {
                              if (isHeaderEdit) {
                                ctx.dispatch({
                                  type: WebServiceActionTypes.SET_PROPERTY,
                                  payload: [
                                    { key: ['name'], value: nameRef.current?.value },
                                    { key: ['description'], value: descriptionRef.current?.value }
                                  ]
                                });

                                setIsHeaderEdit(false);
                              } else {
                                setIsHeaderEdit(true);
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  </FlexLayout>
                </FlexLayout>
                <H3 color="secondary" fontWeight="bold" fontSize="xs" mt="md">
                  DESCRIPTION
                </H3>
                {
                  // eslint-disable-next-line no-nested-ternary
                  !ctx ? (
                    <Loading height="23.4px" />
                  ) : isHeaderEdit ? (
                    <Input
                      type="text"
                      inputRef={descriptionRef}
                      defaultValue={ctx?.state.description}
                      multiline
                      fullWidth
                      minRows={3}
                    />
                  ) : (
                    <H3>{ctx?.state.description}</H3>
                  )
                }
              </Box>
            </FlexLayout>
          </Box>
          <Divider />
          {ctx?.state.schema.type && (
            <Connection type={ctx.state.schema.type} onChange={updateConfig} isEditable={isEditable} />
          )}
          <Divider />
          {ctx?.state.schema && (
            <Configuration
              isEditable={isEditable}
              protocol={ctx.state.schema.protocol}
              contentType={ctx.state.schema.contentType}
              host={ctx.state.schema.host}
              onChange={updateConfig}
            />
          )}
          <Divider />
          <Authentication
            authentication={ctx?.state.schema.authentication}
            onChange={updateConfig}
            isEditable={isEditable}
          />

          <Divider />
          <HTTPHeaders
            key="headers"
            headers={ctx?.state.schema.headers || {}}
            onChange={updateConfig}
            isEditable={isEditable}
          />
          <Divider />
          <PaginationSettings
            pagination={ctx?.state.schema.pagination}
            // onChange={updateConfig}
            isEditable={isEditable}
            modelData={DefinitionLoader.tryLoad('responseStructure', modelData?.getModel.schema)}
          />
        </Box>
      ) : (
        <H4>Loading settings...</H4>
      )}
    </>
  );
};
